import React, { useMemo, useState } from "react";
import { Button, Card, Table } from "antd";
import { DeleteOutlined, ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import BaseTable from "../../../../components/BaseTable";
import Column from "../../../../components/Column";
import Row from "../../../../components/Row";
import ActionHeader from "../../../../components/ActionHeader";
import usePageState from "../../../../hooks/usePageState";
import useConfirmAction from "../../../../hooks/useConfirmAction";
import useForm from "../../../../hooks/useForm";
import * as Yup from "yup";
import AppInputDropdown from "../../../../components/AppInputDropdown";
import useAsyncEffect from "../../../../hooks/useAsyncEffect";
import { useDateRangeSelector } from "../../../../hooks/useDateRangeSelector";
import AppInputDate from "../../../../components/AppInputDate";
import AppInputNumber from "../../../../components/AppInputNumber";
import { currencyFormat } from "../../../../../applications/utils/NumberUtils";
import AppInputText from "../../../../components/AppInputText/AppInputText";
import { DATE_RANGE_OPTION_ENUM } from "../../../../../applications/constants/AppConst";
import {
  CreateOperatingCostRequest,
  FilterOperatingCostRequest,
  OperatingCostModel,
} from "../../../../../infrastructure/model/OperatingCostModel";
import {
  createOperatingCostUseCase,
  deleteOperatingCostUseCase,
  getOperatingCostUseCase,
} from "../../../../../applications/useCase/ApiUseCase/OperatingCostApiUseCase";
import "./OperatingCost.scss";

const COST_TYPE_OPTIONS = [
  {
    label: "Điện",
    value: "Điện",
  },
  {
    label: "Nước",
    value: "Nước",
  },
  {
    label: "Mặt bằng",
    value: "Mặt bằng",
  },
  {
    label: "Nhân viên",
    value: "Nhân viên",
  },
  {
    label: "Marketing",
    value: "Marketing",
  },
  {
    label: "Khác",
    value: "Khác",
  },
];

function OperatingCostManager() {
  const [dataTable, setDataTable] = useState<OperatingCostModel[]>([]);
  const [itemSelected, setItemSelected] = useState<OperatingCostModel>();
  const { dialog: confirmDeleteDialog, confirm: confirmDelete } = useConfirmAction({
    onConfirm: deleteOperatingCost,
  });
  const { isLoading, setLoading, showError, showSuccess, showClientError } = usePageState();
  const { endDate, startDate, DateRangeSelectorComponent, validateDateRange } =
    useDateRangeSelector({
      initSelectOptionDate: DATE_RANGE_OPTION_ENUM.CUSTOM,
    });

  const createRawMaterialSchema = Yup.object({
    expense_date: Yup.number()
      .required("Ngày thanh toán là bắt buộc")
      .moreThan(0, "Ngày thanh toán phải > 0"),
    cost_type: Yup.string().required("Loại chi phí là bắt buộc"),
    amount: Yup.number().required("Số tiền là bắt buộc"),
  });

  const formik = useForm<CreateOperatingCostRequest>({
    initialValues: {
      cost_type: "",
      amount: 0,
      expense_date: 0,
      note: "",
    },
    validationSchema: createRawMaterialSchema,
    onSubmit: async (values,) => {
      const result = await createOperatingCostUseCase({
        request: values,
        setLoading,
        showError,
        showSuccess,
      });
      if (result) {
        setDataTable([result, ...dataTable]);
        formik.resetForm();
      }
    },
  });

  async function getListData() {
    if (!validateDateRange) return;
    const dataRes = await getOperatingCostUseCase({
      request: {
        start_date: startDate!,
        end_date: endDate!,
      },
      setLoading,
      showError,
    });
    setDataTable(dataRes);
    setItemSelected(undefined);
    formik.resetForm();
  }

  async function deleteOperatingCost() {
    if (!itemSelected?.id) return;
    const dataRes = await deleteOperatingCostUseCase({
      id: itemSelected.id,
      setLoading,
      showError,
      showSuccess,
    });
    if (dataRes) {
      setDataTable(dataTable.filter((e) => e.id !== itemSelected.id));
      setItemSelected(undefined);
    }
  }

  useAsyncEffect(async () => {
    await Promise.all([getListData()]);
  }, []);

  const totalFee = useMemo(() => {
    return dataTable.reduce((sum, t) => sum + t.amount, 0);
  }, [dataTable.length]);

  return (
    <Column className="h-100 w-100 p-3">
      <Column className="gap-2 pb-2">
        <Card>
          <Row className="w-100 gap-2">
            <Column className="col-6">
              <AppInputDate
                orientation="col"
                label="Ngày thanh toán"
                isRequired
                currentValue={formik.values.expense_date}
                onChangeValue={(e) => formik.updateFieldValue("expense_date", e!)}
                errorText={formik.getFormErrorMessage("expense_date")}
              />
            </Column>
            <Column className="col-4">
              <AppInputDropdown
                orientation="col"
                label="Loại chi phí"
                options={COST_TYPE_OPTIONS}
                isRequired
                currentValue={formik.values.cost_type}
                onChangeValue={(e) => formik.updateFieldValue("cost_type", e)}
                errorText={formik.getFormErrorMessage("cost_type")}
              />
            </Column>

            <Column className="col-5">
              <AppInputNumber
                orientation="col"
                label="Số tiền"
                currentValue={formik.values.amount}
                onChangeValue={(e) => formik.updateFieldValue("amount", e)}
                errorText={formik.getFormErrorMessage("amount")}
              />
            </Column>
            <Column className="col-5">
              <AppInputText
                orientation="col"
                label="Ghi chú"
                currentValue={formik.values.note}
                onTextChange={(e) => formik.updateFieldValue("note", e)}
              />
            </Column>
            <Button
              type="primary"
              htmlType="submit"
              style={{ marginTop: "25px" }}
              onClick={() => formik.checkErrorAndSubmit(showClientError)}
            >
              Lưu
            </Button>
          </Row>
        </Card>
        <Row className="w-100 gap-2 p-1">
          <DateRangeSelectorComponent />

          <Column style={{ paddingTop: "19px" }}>
            <Button
              icon={<SearchOutlined />}
              type="primary"
              onClick={() => getListData()}
              className="w-100"
            >
              Tìm kiếm
            </Button>
          </Column>
        </Row>
        {/* Danh sách Table */}
        <ActionHeader
          rowCount={dataTable.length}
          actions={[
            {
              icon: <ReloadOutlined />,
              userClick: getListData,
              title: "Làm mới",
              isNormal: true,
            },
            {
              icon: <DeleteOutlined />,
              userClick: confirmDelete,
              title: "Xóa",
              disabled: !itemSelected?.id,
              isNormal: true,
              color: "danger",
            },
          ]}
        />
      </Column>

      <BaseTable
        dataSource={dataTable}
        rowSelection={{
          type: "radio",
          selectedRowKeys: itemSelected?.id ? [itemSelected.id] : [],
          onSelect(record) {
            setItemSelected(record);
          },
        }}
        loading={isLoading}
        columns={[
          {
            title: "Ngày thanh toán",
            dataIndex: "expenseDate",
            key: "expenseDate",
            render(value, record, index) {
              return new Date(value).toLocaleDateString("vi-VN");
            },
            width: "20%",
          },
          {
            title: "Loại chi phí",
            dataIndex: "costType",
            key: "costType",
            width: "20%",
          },
          {
            title: "Số tiền",
            dataIndex: "amount",
            key: "amount",
            render(value, record, index) {
              return currencyFormat(value);
            },
            width: "30%",
          },
          {
            title: "Ghi chú",
            dataIndex: "note",
            key: "note",
            width: "30%",
          },
        ]}
        summaryHeight={32}
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row style={{ height: "32px" }}>
              <Table.Summary.Cell index={0} className=""></Table.Summary.Cell>
              <Table.Summary.Cell index={1} className="summary-cell"></Table.Summary.Cell>
              <Table.Summary.Cell index={2} className="summary-cell">
                Tổng chi phí
              </Table.Summary.Cell>
              <Table.Summary.Cell index={3} className="summary-cell">
                {currencyFormat(totalFee)}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={4} className="summary-cell"></Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        )}
        rowKey="id"
      />
      {confirmDeleteDialog}
    </Column>
  );
}

export default OperatingCostManager;
